// frontend/src/pages/admin/User.tsx
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Container,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  HStack,
  Button,
  Badge,
  Select,
  Card,
  CardBody,
  SimpleGrid,
  useBreakpointValue,
  Divider,
  useToast,
} from '@chakra-ui/react'
import { getUserList, updateUserApprovalStatus } from '../../services/apiService'
import dayjs from 'dayjs'

const adminNavItems = [
  { label: '회원 관리', path: '/admin/user' },
  { label: '커뮤니티 관리', path: '/admin/community' },
  { label: 'Q&A 관리', path: '/admin/qna' },
  { label: '공지사항 관리', path: '/admin/notice' },
  { label: '자산 관리', path: '/admin/asset' },
  { label: '경매사례 관리', path: '/admin/auction-case' },
]

interface User {
  id: number
  account: string
  username: string
  birthDate: string | null
  gender: string | null
  phone: string | null
  email: string | null
  isAdmin: number
  isApproved: number
  createdAt: string
  updatedAt: string | null
}

const User = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [processingUserId, setProcessingUserId] = useState<number | null>(null)

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const { data: res } = await getUserList(page, limit)
      if (res.statusCode === 200) {
        setUsers(res.data.users)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [page, limit])

  const handleApproval = async (userId: number) => {
    setProcessingUserId(userId)
    try {
      const { data: res } = await updateUserApprovalStatus(userId, true)
      if (res.statusCode === 200) {
        toast({
          title: '승인 완료',
          description: '회원 승인이 완료되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        fetchUsers()
      } else {
        toast({
          title: '승인 실패',
          description: res.message || '회원 승인 중 오류가 발생했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } catch (error: any) {
      toast({
        title: '승인 실패',
        description: error.response?.data?.message || '회원 승인 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setProcessingUserId(null)
    }
  }

  return (
    <DefaultLayout>
      <AdminNav items={adminNavItems} />
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg">회원 관리</Heading>

          <Box
            p={6}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    총 {total}명의 회원
                  </Text>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value))
                      setPage(1)
                    }}
                    width="120px"
                    size="sm"
                  >
                    <option value={10}>10개씩</option>
                    <option value={20}>20개씩</option>
                    <option value={50}>50개씩</option>
                  </Select>
                </HStack>

                {isMobile ? (
                  // 모바일: 카드 형태
                  <VStack spacing={4} align="stretch">
                    {users.length === 0 ? (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">등록된 회원이 없습니다.</Text>
                      </Box>
                    ) : (
                      users.map((user) => (
                        <Card key={user.id} size="sm">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <Text fontWeight="bold" fontSize="lg">
                                  {user.username}
                                </Text>
                                {user.isAdmin === 1 ? (
                                  <Badge colorScheme="brand">관리자</Badge>
                                ) : (
                                  <Badge colorScheme="gray">일반</Badge>
                                )}
                              </HStack>
                              <Divider />
                              <SimpleGrid columns={2} spacing={2} fontSize="sm">
                                <Text color="gray.600">ID:</Text>
                                <Text>{user.id}</Text>
                                <Text color="gray.600">아이디:</Text>
                                <Text>{user.account}</Text>
                                <Text color="gray.600">생년월일:</Text>
                                <Text>{user.birthDate || '-'}</Text>
                                <Text color="gray.600">성별:</Text>
                                <Text>
                                  {user.gender === 'M' ? '남성' : user.gender === 'F' ? '여성' : '-'}
                                </Text>
                                <Text color="gray.600">핸드폰:</Text>
                                <Text>{user.phone || '-'}</Text>
                                <Text color="gray.600">이메일:</Text>
                                <Text>{user.email || '-'}</Text>
                                <Text color="gray.600">가입일시:</Text>
                                <Text>
                                  {dayjs(user.createdAt).format('YYYY-MM-DD HH:mm')}
                                </Text>
                                <Text color="gray.600">승인 상태:</Text>
                                <Badge colorScheme={user.isApproved === 1 ? 'green' : 'orange'}>
                                  {user.isApproved === 1 ? '승인 완료' : '승인 대기'}
                                </Badge>
                              </SimpleGrid>
                              {user.isApproved !== 1 && (
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  onClick={() => handleApproval(user.id)}
                                  isLoading={processingUserId === user.id}
                                >
                                  승인하기
                                </Button>
                              )}
                            </VStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                ) : (
                  // 데스크탑: 테이블 형태
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>ID</Th>
                          <Th>아이디</Th>
                          <Th>사용자명</Th>
                          <Th>생년월일</Th>
                          <Th>성별</Th>
                          <Th>핸드폰</Th>
                          <Th>이메일</Th>
                          <Th>관리자</Th>
                          <Th>가입일시</Th>
                          <Th>승인 상태</Th>
                          <Th>관리</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {users.length === 0 ? (
                          <Tr>
                            <Td colSpan={11} textAlign="center" py={8}>
                              <Text color="gray.500">등록된 회원이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          users.map((user) => (
                            <Tr key={user.id}>
                              <Td>{user.id}</Td>
                              <Td>{user.account}</Td>
                              <Td>{user.username}</Td>
                              <Td>{user.birthDate || '-'}</Td>
                              <Td>
                                {user.gender === 'M' ? '남성' : user.gender === 'F' ? '여성' : '-'}
                              </Td>
                              <Td>{user.phone || '-'}</Td>
                              <Td>{user.email || '-'}</Td>
                              <Td>
                                {user.isAdmin === 1 ? (
                                  <Badge colorScheme="brand">관리자</Badge>
                                ) : (
                                  <Badge colorScheme="gray">일반</Badge>
                                )}
                              </Td>
                              <Td>
                                {dayjs(user.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Td>
                              <Td>
                                <Badge colorScheme={user.isApproved === 1 ? 'green' : 'orange'}>
                                  {user.isApproved === 1 ? '승인 완료' : '승인 대기'}
                                </Badge>
                              </Td>
                              <Td>
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  onClick={() => handleApproval(user.id)}
                                  isDisabled={user.isApproved === 1 || user.isAdmin === 1}
                                  isLoading={processingUserId === user.id}
                                >
                                  승인하기
                                </Button>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {totalPages > 0 && (
                  <HStack justify="center" spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      isDisabled={page === 1}
                    >
                      이전
                    </Button>
                    <Text fontSize="sm">
                      {page} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      isDisabled={page >= totalPages}
                    >
                      다음
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default User
